import type { DocNode as ADF } from '@atlaskit/adf-schema';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { traverse } from '@atlaskit/adf-utils/traverse';
import { eventHub, findEditorId } from '@atlassian/editor-plugin-ai/EventHub';
import { fg } from '@atlassian/jira-feature-gating';
import type { AdditionalPayload, EditorExperience } from './types.tsx';

const GIC_ISSUE_DESCRIPTION_EDITOR_ID = 'issue-create.common.ui.fields.description-field.wrapper';
const ISSUE_DESCRIPTION_EDITOR_ID = 'issue-description-editor';

const issueViewEditorSelector = `[data-editor-container-id="${ISSUE_DESCRIPTION_EDITOR_ID}"]`;

const gicEditorSelector = `[data-testid="${GIC_ISSUE_DESCRIPTION_EDITOR_ID}"]`;

const LINK_SOURCE_TYPE = {
	CONFLUENCE: 'CONFLUENCE_LINK',
	LOOM: 'LOOM_LINK',
	GOOGLE_DOC: 'GOOGLE_DOC_LINK',
} as const;

export const isLoomLink = (url = ''): boolean => {
	const pattern = /loom\.com\/share\/.+/;
	return pattern.test(url);
};

export const isGoogleDocLink = (url = ''): boolean => {
	const pattern = /docs\.google\.com\/document\/d\/[a-zA-Z0-9_-]+\/edit/;
	return pattern.test(url);
};

export const triggerDescriptionCompleterStreamHubEvent = (
	editorExperience: EditorExperience,
	url = '',
): boolean => {
	const editorSelector =
		editorExperience === 'IssueView' ? issueViewEditorSelector : gicEditorSelector;
	const targetEditorId = findEditorId(editorSelector);

	let source;
	if (isLoomLink(url)) {
		source = LINK_SOURCE_TYPE.LOOM;
	} else if (isGoogleDocLink(url) && fg('thor_support_google_docs_in_description_improver')) {
		source = LINK_SOURCE_TYPE.GOOGLE_DOC;
	} else {
		source = LINK_SOURCE_TYPE.CONFLUENCE;
	}
	const context = { primary_link: url };

	if (targetEditorId) {
		eventHub.publishToEditor({
			event: 'start prompt',
			data: {
				prompt: 'description completer',
				targetEditorId,
				analyticSourceId: 'jiraProactiveDescriptionCompleterFooterButton',
				additionalPayloadData: { source, context },
			},
		});
		return true;
	}
	return false;
};

export const triggerEditorStreamHubEvent = (
	additionalRequestPayload: AdditionalPayload,
	editorExperience: EditorExperience,
): boolean => {
	const editorSelector =
		editorExperience === 'IssueView' ? issueViewEditorSelector : gicEditorSelector;
	const targetEditorId = findEditorId(editorSelector);
	if (targetEditorId) {
		eventHub.publishToEditor({
			event: 'start prompt',
			data: {
				prompt: 'issue reformatter',
				targetEditorId,
				analyticSourceId: 'jiraProactiveDescriptionCompleterFooterButton',
				additionalPayloadDataIssueReformatter: { ...additionalRequestPayload },
			},
		});
		return true;
	}
	return false;
};

export const isEligibleLink = (url = ''): boolean => {
	let pattern;
	if (fg('thor_support_google_docs_in_description_improver')) {
		pattern =
			// Match Confluence, Loom, or Google Doc link
			/\/wiki\/spaces\/[^/]+\/(?:pages|blog)\/|loom\.com\/share\/.+|docs\.google\.com\/document\/d\/[a-zA-Z0-9_-]+\/edit/;
	} else {
		pattern =
			// Match Confluence or Loom link
			/\/wiki\/spaces\/[^/]+\/(?:pages|blog)\/|loom\.com\/share\/.+/;
	}
	return pattern.test(url);
};

export const getAnalyticsIssueType = (issueType: string | null): string => {
	if (!issueType) return 'custom';
	const allowedIssueTypeValues = ['bug', 'story', 'task', 'subtask', 'epic'];
	const issueTypeLabel = issueType.toLowerCase();
	return allowedIssueTypeValues.includes(issueTypeLabel) ? issueTypeLabel : 'custom';
};

// A faster version to count ADF chars, using traverse instead of reduce
export const countADFChars = (value: ADF | undefined): number => {
	if (!value) return 0;

	let chars = 0;
	traverse(value, {
		text: (node: ADFEntity) => {
			if (node.text) {
				chars += node.text.length;
			}
		},
		emoji: ({ attrs }) => {
			if (attrs?.text) {
				chars += attrs.text.length;
			}
		},
		paragraph: () => {
			chars += 1;
		},
	});

	// Subtract 1 due to doc node
	return chars ? chars - 1 : chars;
};
